/**
 * Support — public route, no auth required.
 *
 * Serves as the App Store Connect "Support URL" so Apple/Google reviewers
 * (and end users) can reach us without an account. Must remain outside
 * ProtectedRoute and above the catch-all in App.js.
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, HelpCircle, Clock } from 'lucide-react';
import useSmartBack from '../lib/useSmartBack';

const SUPPORT_EMAIL = 'support@futureself.es';

const Section = ({ title, children, testId }) => (
  <section className="mb-8" data-testid={testId}>
    <h2 className="font-serif text-2xl text-[#DCD3CA] mb-3">{title}</h2>
    <div className="text-[#B5B1AB] font-sans text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const Card = ({ children, testId }) => (
  <div
    className="rounded-2xl border border-white/5 bg-[#252528] p-6"
    data-testid={testId}
  >
    {children}
  </div>
);

const Support = () => {
  const smartBack = useSmartBack();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof document !== 'undefined') {
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }
  }, []);
  useEffect(() => {
    const prev = document.title;
    document.title = 'Future Self | Support';
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div
      className="min-h-[100dvh] bg-[#1C1C1E] text-[#DCD3CA]"
      style={{
        paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
        paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
      }}
      data-testid="support-page"
    >
      <div className="max-w-2xl mx-auto px-6">
        <button
          type="button"
          onClick={smartBack}
          className="inline-flex items-center gap-2 text-[#8BAA9E] font-sans text-sm mb-8 hover:text-[#A3C2B6]"
          data-testid="support-back-link"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Future Self
        </button>

        <header className="mb-10">
          <p className="text-[#8BAA9E] font-sans text-xs uppercase tracking-[2px] mb-3">FutureSelf</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F5F3EF] mb-3">Support</h1>
          <p className="text-[#B5B1AB] font-sans text-base">We&rsquo;re here to help.</p>
        </header>

        <Section title="Get in touch" testId="support-section-intro">
          <p>
            If you have any questions, experience technical issues, or need assistance with your Future Self
            account, we&rsquo;d love to hear from you.
          </p>
        </Section>

        <Card testId="support-contact-card">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#8BAA9E]/10 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-[#8BAA9E]" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <p className="text-[#7A7670] font-sans text-xs uppercase tracking-[2px] mb-2">
                Contact Support
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-serif text-2xl text-[#F5F3EF] hover:text-[#8BAA9E] transition-colors break-all"
                data-testid="support-email-link"
              >
                {SUPPORT_EMAIL}
              </a>
              <p className="text-[#B5B1AB] font-sans text-sm mt-3">
                Tap the email address on mobile to open your email app.
              </p>
            </div>
          </div>
        </Card>

        <div className="h-8" />

        <Card testId="support-topics-card">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#8BAA9E]/10 flex items-center justify-center shrink-0">
              <HelpCircle className="w-5 h-5 text-[#8BAA9E]" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <p className="text-[#7A7670] font-sans text-xs uppercase tracking-[2px] mb-3">
                We can help with
              </p>
              <ul className="text-[#B5B1AB] font-sans text-sm leading-relaxed space-y-2">
                <li>• Account issues</li>
                <li>• Subscription questions</li>
                <li>• Password resets</li>
                <li>• Technical problems</li>
                <li>• Feedback and feature suggestions</li>
                <li>• General enquiries</li>
              </ul>
            </div>
          </div>
        </Card>

        <div className="h-8" />

        <Card testId="support-response-card">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#8BAA9E]/10 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-[#8BAA9E]" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <p className="text-[#7A7670] font-sans text-xs uppercase tracking-[2px] mb-2">
                Response Time
              </p>
              <p className="text-[#DCD3CA] font-sans text-base">
                We aim to respond within <span className="text-[#F5F3EF]">2 business days</span>.
              </p>
            </div>
          </div>
        </Card>

        <div className="h-10" />

        <Section title="Helpful links" testId="support-section-links">
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/privacy"
              className="inline-flex items-center justify-center px-5 py-3 rounded-full border border-white/10 bg-white/5 text-[#DCD3CA] font-sans text-sm hover:bg-white/10 hover:text-[#F5F3EF] transition-colors"
              data-testid="support-privacy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="inline-flex items-center justify-center px-5 py-3 rounded-full border border-white/10 bg-white/5 text-[#DCD3CA] font-sans text-sm hover:bg-white/10 hover:text-[#F5F3EF] transition-colors"
              data-testid="support-terms-link"
            >
              Terms &amp; Conditions
            </Link>
          </div>
        </Section>

        <footer
          className="pt-8 mt-8 border-t border-white/5 text-[#7A7670] font-sans text-xs"
          data-testid="support-footer"
        >
          <p>&copy; Future Self. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Support;
